import { FileOrigin } from '@flatfile/api'
import * as XLSX from 'xlsx'
import { getAuthToken } from '../../auth/guestAuth'
import { AppHTTPService } from '../http-service'

const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
/**
 * Prepare a multipart form upload to Flatfile for a specific file
 *
 * @todo handle an upload error properly
 * @param file - The file to upload
 * @param setUploadProgress - Callback function to update upload progress
 * @param spaceId - The space ID to upload to
 * @param environmentId - The environment ID
 * @param origin - Optional file origin information
 */
export async function uploadFileToSpace(
  file: File,
  setUploadProgress: (p: UploadProgress) => void,
  calculateUploadProgress: (
    progressEvent: any,
    startTime: number
  ) => UploadProgress,
  environmentId: string,
  projectId: string,
  origin?: FileOrigin
) {
  // Use the new helper in http-service
  const start = Date.now()
  return AppHTTPService.client.files.create(
    file,
    environmentId,
    projectId,
    origin,
    (progress) => {
      // Use the provided calculateUploadProgress for compatibility
      const fakeEvent = { loaded: progress, total: 100 }
      setUploadProgress(calculateUploadProgress(fakeEvent, start))
    }
  )
}

export interface UploadProgress {
  progress: number
  speed: number
}

export async function performSheetUpload(
  file: File,
  fileId: string,
  spaceId: string
) {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'buffer' })

  // If there's only one sheet and it's named "Sheet1", use the file name instead
  const useFileName =
    workbook.SheetNames.length === 1 && workbook.SheetNames[0] === 'Sheet1'

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName]
    const sheetData = XLSX.utils.sheet_to_json(sheet)

    // Skip empty sheets
    if (!sheetData || sheetData.length === 0) {
      continue
    }

    const firstEntry = sheetData[0]
    const headers = Object.keys(firstEntry)
    const blueprint = {
      fields: headers.map((h) => ({ key: h, label: h, type: 'string' })),
    }

    // Use file name for single sheet Excel files, otherwise use sheet name
    const finalSheetName = useFileName
      ? file.name.replace(/\.(xlsx?|xls)$/i, '')
      : sheetName

    const sheetResponse = await AppHTTPService.client.sheets.create({
      fileId: fileId,
      name: finalSheetName,
      schema: blueprint,
    })
    const sheetId = sheetResponse.data.id
    const totalRecords = sheetData.length
    const pageSize = 2_000_000
    const pageCount = Math.ceil(totalRecords / pageSize)

    for (let page = 0; page < pageCount; page++) {
      const records = sheetData
        .slice(page * pageSize, (page + 1) * pageSize)
        .map((r) => {
          return Object.fromEntries(
            Object.entries(r).map(([k, v]) => [k, v.toString()])
          )
        })
      const body = records
        .map((record) =>
          headers
            .map(
              (column) =>
                `"${String(record[column] ?? '')
                  .replace('\\', '\\\\')
                  .replace('"', '\\"')}"`
            )
            .join(',')
        )
        .join('\n')
        .concat('\n')
      // Use getAuthToken for auth
      const token = await getAuthToken()
      await fetch(`${VITE_API_URL}/sheets/${sheetId}/records/csv2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/x-csv2',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body,
      })
    }
  }
}
